import { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useMarketplaceConnectionAttentionCount } from "../../hooks/useMarketplaceConnectionAttentionCount";
import { useMarketplaceRequestCount } from "../../hooks/useMarketplaceRequestCount";
import {
  MARKETPLACE_SIDEBAR_DESTINATIONS,
  type MarketplaceSidebarDestination,
} from "../layout/nav";

/**
 * Phone-width Marketplace navigation — passed as `pageMobileNav` to ProLayout
 * alongside `MarketplaceSidebar` on every marketplace route (plan 443 M2).
 *
 * The page sidebar is `hidden md:block`, so below 768px a pro had no way to move
 * between marketplace pages. This is the same six destinations (one source:
 * `MARKETPLACE_SIDEBAR_DESTINATIONS`, no group labels) as a horizontally
 * scrollable chip strip. Overflow scrolls inside the strip, never the page.
 * Badge counts come from the sidebar's own hooks.
 */
function Badge({ testId, count }: { testId: string; count: number }) {
  return (
    <span
      data-testid={testId}
      className="rounded-full bg-pro px-1.5 py-px text-[10px] font-bold text-white"
    >
      {count}
    </span>
  );
}

export function MarketplaceMobileNav() {
  const { pathname } = useLocation();
  const requestBadge = useMarketplaceRequestCount();
  const attentionBadge = useMarketplaceConnectionAttentionCount();
  const activeRef = useRef<HTMLAnchorElement | null>(null);

  // Keep the active chip in view when it sits past the strip's right edge.
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: "nearest", inline: "center" });
  }, [pathname]);

  const isActive = (dest: MarketplaceSidebarDestination) =>
    (dest.matchPrefixes ?? [dest.to]).some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );

  return (
    <nav
      aria-label="Marketplace pages"
      className="min-w-0 border-b border-border-soft bg-[color-mix(in_oklch,var(--sill-bg-warm)_60%,var(--sill-card))] md:hidden"
    >
      <div
        data-testid="marketplace-mobile-nav-strip"
        className="flex min-w-0 gap-2 overflow-x-auto px-3 py-2"
      >
        {MARKETPLACE_SIDEBAR_DESTINATIONS.map((dest) => {
          const active = isActive(dest);
          const Icon = dest.icon;
          if (dest.disabled) {
            // Non-navigable placeholder, matching the sidebar — never a link
            // to a blank/404 route.
            return (
              <div
                key={dest.id}
                aria-disabled="true"
                title="Coming soon"
                className="inline-flex shrink-0 cursor-not-allowed select-none items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-fg-muted opacity-45"
              >
                <Icon className="size-4 shrink-0" />
                <span>{dest.label}</span>
              </div>
            );
          }
          return (
            <NavLink
              key={dest.id}
              to={dest.to}
              ref={active ? activeRef : undefined}
              aria-current={active ? "page" : undefined}
              className={[
                "inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-sm no-underline outline-none",
                "focus-visible:ring-2 focus-visible:ring-pro/30",
                active
                  ? "bg-white font-semibold text-fg shadow-[0_1px_3px_rgba(15,23,42,0.10)] ring-1 ring-border/60"
                  : "font-medium text-fg-muted hover:bg-white/60 hover:text-fg",
              ].join(" ")}
            >
              <Icon className="size-4 shrink-0" />
              <span>{dest.label}</span>
              {dest.id === "marketplace-requests" && requestBadge > 0 ? (
                <Badge
                  testId="marketplace-requests-badge"
                  count={requestBadge}
                />
              ) : null}
              {dest.id === "marketplace-connections" && attentionBadge > 0 ? (
                <Badge
                  testId="marketplace-connections-badge"
                  count={attentionBadge}
                />
              ) : null}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
